import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Shield, AlertTriangle, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export const TwoFactorAuthSettings = ({ user }) => {
  const [twoFAStatus, setTwoFAStatus] = useState(null);
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  
  // Safe defaults to prevent undefined errors
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [setupLoading, setSetupLoading] = useState(false);

  useEffect(() => {
    fetchTwoFAStatus();
  }, []);

  const fetchTwoFAStatus = async () => {
    try {
      const response = await axios.get(`${API}/api/auth/2fa/status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setTwoFAStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch 2FA status:', error);
      // Set safe default so component doesn't crash
      setTwoFAStatus({ enabled: false, backup_codes_remaining: 0 });
    }
  };

  const handleSetup2FA = async () => {
    setSetupLoading(true);
    try {
      const response = await axios.post(`${API}/api/auth/2fa/setup`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Safe extraction with fallbacks
      const qrCodeData = response.data?.qr_code ?? response.data?.qr_code_data_url ?? '';
      const secretData = response.data?.secret ?? '';
      const backupCodesData = Array.isArray(response.data?.backup_codes) 
        ? response.data.backup_codes 
        : [];
      
      setQrCode(qrCodeData);
      setSecret(secretData);
      setBackupCodes(backupCodesData);
      
      setSetupDialogOpen(false);
      setVerifyDialogOpen(true);
    } catch (error) {
      console.error('2FA Setup Error:', error);
      
      // Normalize error message to string
      let errorMessage = 'Fehler beim Einrichten von 2FA';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle different error formats
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.detail) {
          // FastAPI detail field
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            // Pydantic validation errors
            errorMessage = errorData.detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
          } else if (errorData.detail.msg) {
            errorMessage = errorData.detail.msg;
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.msg) {
          errorMessage = errorData.msg;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setSetupLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Bitte geben Sie einen 6-stelligen Code ein');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/auth/2fa/enable`, {
        totp_code: verificationCode  // Backend expects 'totp_code', not 'code'
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      toast.success('2FA erfolgreich aktiviert!');
      
      // Show backup codes if returned
      if (response.data?.backup_codes && Array.isArray(response.data.backup_codes)) {
        setBackupCodes(response.data.backup_codes);
        // Keep dialog open to show backup codes
        toast.success('Backup-Codes wurden generiert. Bitte sicher aufbewahren!');
      }
      
      setVerifyDialogOpen(false);
      setVerificationCode('');
      fetchTwoFAStatus();
    } catch (error) {
      console.error('2FA Verify Error:', error);
      
      // Normalize error message to string
      let errorMessage = 'Code ungültig';
      
      if (error.response?.status === 400) {
        // Backend validation error
        errorMessage = 'Code ungültig. Bitte prüfen Sie Ihre Authenticator-App.';
      } else if (error.response?.data) {
        const errorData = error.response.data;
        
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            // Pydantic validation errors - extract field names
            const fieldErrors = errorData.detail.map(err => {
              const field = err.loc ? err.loc[err.loc.length - 1] : 'Feld';
              const msg = err.msg || 'erforderlich';
              return `${field}: ${msg}`;
            });
            errorMessage = fieldErrors.join(', ');
          } else if (errorData.detail.msg) {
            errorMessage = errorData.detail.msg;
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.msg) {
          errorMessage = errorData.msg;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!twoFAStatus) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground mt-2">2FA-Status wird geladen...</p>
        </CardContent>
      </Card>
    );
  }

  const is2FAEnabled = twoFAStatus?.enabled ?? false;
  const isSuperadmin = user?.role === 'superadmin';
  const backupCodesRemaining = twoFAStatus?.backup_codes_remaining ?? 0;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Zwei-Faktor-Authentifizierung (2FA)
            {is2FAEnabled && <Check className="h-5 w-5 text-green-600" />}
          </CardTitle>
          <CardDescription>
            {isSuperadmin ? (
              <span className="text-amber-600 font-medium flex items-center gap-1">
                <AlertTriangle className="h-4 w-4" />
                2FA ist für Superadmin-Accounts verpflichtend
              </span>
            ) : (
              'Zusätzliche Sicherheit für Ihren Account'
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {is2FAEnabled ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-green-600">
                <Check className="h-5 w-5" />
                <span className="font-medium">2FA ist aktiviert</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Backup-Codes übrig: {backupCodesRemaining}
              </p>
              <p className="text-sm text-muted-foreground">
                Sie benötigen einen 6-stelligen Code aus Ihrer Authenticator-App bei jedem Login.
              </p>
              {isSuperadmin && (
                <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg p-3 text-sm text-amber-900 dark:text-amber-100">
                  2FA kann für Superadmin-Accounts nicht deaktiviert werden.
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                2FA ist derzeit nicht aktiviert.
              </p>
              {isSuperadmin && (
                <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg p-3">
                  <p className="text-sm text-red-900 dark:text-red-100 font-medium flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Aktivierung erforderlich
                  </p>
                  <p className="text-sm text-red-800 dark:text-red-200 mt-1">
                    Sie müssen 2FA aktivieren, um sich weiterhin als Superadmin anmelden zu können.
                  </p>
                </div>
              )}
              <Button onClick={() => setSetupDialogOpen(true)}>
                2FA jetzt aktivieren
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Setup Confirmation Dialog */}
      <Dialog open={setupDialogOpen} onOpenChange={setSetupDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>2FA einrichten</DialogTitle>
            <DialogDescription>
              Sie benötigen eine Authenticator-App (z.B. Google Authenticator, Authy, Microsoft Authenticator).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm">
              Nach dem Fortfahren erhalten Sie einen QR-Code zum Scannen mit Ihrer Authenticator-App.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSetupDialogOpen(false)} disabled={setupLoading}>
              Abbrechen
            </Button>
            <Button onClick={handleSetup2FA} disabled={setupLoading}>
              {setupLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Wird eingerichtet...
                </>
              ) : (
                'Fortfahren'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Verify Dialog with QR Code */}
      <Dialog open={verifyDialogOpen} onOpenChange={setVerifyDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>2FA aktivieren</DialogTitle>
            <DialogDescription>
              Scannen Sie den QR-Code mit Ihrer Authenticator-App
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* QR Code */}
            {qrCode && (
              <div className="flex justify-center">
                <img src={qrCode} alt="QR Code" className="w-48 h-48" />
              </div>
            )}

            {/* Manual Entry Secret */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">
                Manueller Eintrag (falls QR-Scan nicht möglich):
              </Label>
              <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded text-xs font-mono break-all">
                {secret}
              </div>
            </div>

            {/* Backup Codes - Safe rendering */}
            {Array.isArray(backupCodes) && backupCodes.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-amber-600">
                  Backup-Codes (sicher aufbewahren!)
                </Label>
                <div className="grid grid-cols-2 gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 rounded border border-amber-200">
                  {backupCodes.map((code, idx) => (
                    <code key={idx} className="text-xs font-mono">{code}</code>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Diese Codes ermöglichen den Zugriff, wenn Sie Ihre Authenticator-App verlieren.
                </p>
              </div>
            )}

            {/* Verification Code Input */}
            <div className="space-y-2">
              <Label htmlFor="verify-code">
                Code aus Authenticator-App eingeben
              </Label>
              <Input
                id="verify-code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="000000"
                maxLength={6}
                className="text-center text-lg tracking-widest font-mono"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVerifyDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleVerify2FA} disabled={loading || verificationCode.length !== 6}>
              {loading ? 'Wird verifiziert...' : '2FA aktivieren'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
